import { Flex, Skeleton, Stack, Text, Button, VStack, useToast } from '@chakra-ui/react';
import { Account } from '@/model';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { useEffect } from 'react';
import CenteredSpinner from '@/components/CenterSpinner/CenteredSpinner';
import { InputField } from '@/components/InputField';
import { InputPhone } from '@/components/ControlledInput';
import { ModalFormData, modalFormSchema } from './schemas';
import { usePutAccountContactMutation } from '@/services/queries/account';
import { PutAccountContactRequest } from '@/services/request/account';
import { useTranslation } from 'react-i18next';

export interface ContactTabProps {
  isLoading?: boolean;
  item: Account;
}

export const ContactTab = ({ isLoading, item }: ContactTabProps) => {
  const putItemMutation = usePutAccountContactMutation();
  const {
    control,
    handleSubmit,
    register,
    setValue,
    formState: { errors },
  } = useForm<ModalFormData>({
    defaultValues: {
      email: item?.email,
      mobile_number: item?.mobile_number,
    },
    resolver: zodResolver(modalFormSchema),
  });
  const toast = useToast();
  const { t } = useTranslation();

  const onSubmit = (data: ModalFormData) => {
    const payload: PutAccountContactRequest = {
      email: data.email,
      mobile_number: data.mobile_number,
    };

    putItemMutation.mutate(
      { id: item.uuid, payload },
      {
        onSuccess: () => {
          toast({
            status: 'success',
            title: t('pages.accountdetail.components.tabs.contacttab.success'),
          });
        },
        onError: (resp: any) => {
          toast({
            status: 'error',
            title: (resp && resp.message) || t('pages.accountdetail.components.tabs.contacttab.error'),
          });
        },
      },
    );
  };

  useEffect(() => {
    if (!item) return;

    setValue('email', item?.email);
    setValue('mobile_number', item?.mobile_number);
  }, [item, setValue]);

  return (
    <Flex direction='column' gap='1.5rem'>
      {isLoading ? (
        <Skeleton w='160px' h='21px' />
      ) : (
        <Text fontWeight='bold'>{t('pages.accountdetail.components.tabs.contacttab.title')}</Text>
      )}

      <Stack flex={1}>
        <VStack as='form' gap='kilo' align='start' onSubmit={handleSubmit(onSubmit)} maxWidth={{ base: '100%', md: '30%' }}>
          {isLoading ? (
            <>
              <Skeleton width='100%' h='40px' />
              <Skeleton width='100%' h='40px' />
            </>
          ) : (
            <>
              <InputField
                withoutFloating
                label={t('pages.accountdetail.components.tabs.contacttab.email_label')}
                type='email'
                rootProps={{ isInvalid: !!errors?.email, errorMessage: errors?.email?.message }}
                {...register('email')}
              />
              <InputPhone
                withoutFloating
                name='mobile_number'
                control={control}
                label={t('pages.accountdetail.components.tabs.contacttab.phone_label')}
                rootProps={{ isInvalid: !!errors?.mobile_number, errorMessage: errors?.mobile_number?.message }}
              />
            </>
          )}

          {isLoading ? (
            <Skeleton width='138px' h='40px' />
          ) : (
            <Button type='submit'> {t('pages.accountdetail.components.tabs.contacttab.button_submit')}</Button>
          )}
        </VStack>
      </Stack>

      <CenteredSpinner isLoading={putItemMutation.isLoading} />
    </Flex>
  );
};
